import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Settings, Video } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

import AvailabilityAndHolidays from '../../components/appointments/AvailabilityAndHolidays';
import BookingSettings from '../../components/appointments/BookingSettings';
import MyCalendar from '../../components/appointments/MyCalendar';
import VideoConferenceSettings from '../../components/appointments/VideoConferenceSettings';

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState('calendar');

  const showToast = (message, type = 'info') => {
    if (type === 'success') toast.success(message);
    else if (type === 'error') toast.error(message);
    else toast.info(message);
  };

  useEffect(() => {
    const parts = location.pathname.split('/');
    const tab = parts[parts.length - 1];
    if (tab && tab !== 'rendez-vous') {
      setActiveTab(tab);
    }
  }, [location]);

  const tabs = useMemo(() => ([
    {
      id: 'calendar',
      name: t('appointments.tabs.calendar'),
      icon: Calendar,
      component: MyCalendar
    },
    {
      id: 'availability',
      name: t('appointments.tabs.availability'),
      icon: Clock,
      component: AvailabilityAndHolidays
    },
    {
      id: 'settings',
      name: t('appointments.tabs.settings'),
      icon: Settings,
      component: BookingSettings
    },
    {
      id: 'video',
      name: t('appointments.tabs.video'),
      icon: Video,
      component: VideoConferenceSettings
    }
  ]), [t]);

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || MyCalendar;

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    navigate(`/dashboard/rendez-vous/${tabId}`);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('appointments.title', 'Rendez-vous')}
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('appointments.subtitle', 'Gérez vos rendez-vous et disponibilités')}
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-2 flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          );
        })}
      </div>

      <motion.section
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <ActiveComponent showToast={showToast} user={user} />
      </motion.section>
    </div>
  );
};

export default AppointmentsPage;
